import {
  DEFAULT_GRID_HEIGHT,
  ENTITY_TYPES,
  FieldList,
  IFormGroupRules,
  IFormRules,
  NO_ROWS_OVERLAY,
  WIDGET_COMPONENT,
  XUX_GRID_ACTIONS,
} from '@app/shared/types';
import { ORDER_STATUS_VALUE } from '@app/shared/types/enums/order-status.enum';
import {
  DEFAULT_ITEM_LIST_TABLE_HEIGHT,
  INPUT_MAX_LENGTH,
  MAX_INT_NUMBER,
  WIDGET_FAMILY,
} from '@config/global-constants';
import { get, keys } from 'lodash';

import { OrdersEditContainerComponent } from '@containers/orders/containers';
import { COMMON_WARN_MESSAGE_CLASS } from '@app/shared/constants/common-warn-grid-setting';
import {
  generateMaximumByPreferencesPrecision,
  getScanningHighlightRowClass,
} from '@shared/utils';
import { OrderItemFormService } from '@shared/services';
import { getListGridRowCellClass } from '@shared/helpers/ag-grid-helpers';
import { TOOLBAR_BUTTON_POSITION } from '@shared/types';
import { I18n } from '@app/i18n/strings-keys';

export const createFormRules = (
  formService: OrderItemFormService,
  editor: OrdersEditContainerComponent,
): IFormRules => {
  const totalsFormRules = editor.totalFromService.getTotalsFormRules(
    editor.formFieldPath,
    editor.isAddAdditionalTotalFormColumns$,
  );

  const vendorPanelFields: FieldList = {
    type: {
      show: false,
      defaultValue: 'delivery',
    },
    store_number: {
      show: false,
    },
    total_cost: {
      omit: true,
    },
    vendor_name: {
      show: false,
      trim: true,
      minLength: 0,
      required: false,
    },
    vendor_entity_id: {
      title: I18n.EntityNames.Vendor,
      trim: true,
      required: true,
      disabledObs: editor.hiddenVendor$,
      widget: WIDGET_COMPONENT.entity_select,
      widgetData: {
        dataSource: formService.vendorDataSource,
        lazy: true,
        artificialInputCheck: true,
      },
    },
    site_id: {
      title: I18n.EntityNames.Warehouse,
      disabledObs: editor.hiddenVendor$,
      widget: WIDGET_COMPONENT.entity_select,
      widgetFamily: WIDGET_FAMILY.XUX,
      widgetData: {
        entity: ENTITY_TYPES.SITE,
        dataSource: formService.siteIdDataSource,
        lazy: true,
        enableClientFiltering: true,
        artificialInputCheck: true,
      },
      validators: [editor.validationService.requiredValidator],
    },
    transaction_date: {
      title: I18n.Forms.OrderColumns.OrderDate,
      required: true,
      widgetData: {
        minimumObs: editor.orderDateLimit.minimumObs,
        maximumObs: editor.orderDateLimit.maximumObs,
      },
      disabledObs: editor.datePickerDisabled$,
      widget: WIDGET_COMPONENT.xux_datepicker,
      validators: [
        editor.validationService.requiredValidator,
        formService
          .getOrderDateRangeValidator(
            editor,
            editor.formFieldPath.deliveryDate,
            editor.orderDateLimit.minimum,
          )
          .bind(editor),
      ],
    },
    delivery_date: {
      title: I18n.Common.DeliveryDate,
      required: true,
      widgetData: {
        minimumObs: editor.deliveryDateLimit.minimumObs,
      },
      disabledObs: editor.datePickerDisabled$,
      widget: WIDGET_COMPONENT.xux_datepicker,
      validators: [
        editor.validationService.requiredValidator,
        formService
          .getDeliveryDateRangeValidator(
            editor,
            editor.formFieldPath.orderDate,
            editor.orderDateLimit.minimum,
          )
          .bind(editor),
      ],
    },
    external_order_number: {
      title: I18n.Forms.OrderColumns.ExternalReferenceNumber,
      required: false,
      widget: WIDGET_COMPONENT.xux_xiv_input,
      trim: true,
      disabled: (): boolean => editor.handleExternalOrderNumber(),
      validators: [editor.validationService.maxLength(INPUT_MAX_LENGTH)],
      widgetData: {
        artificialInputCheck: true,
      },
    },
    invoice_number: {
      title: I18n.Forms.OrderColumns.InvoiceNumber,
      required: false,
      widget: WIDGET_COMPONENT.xux_xiv_input,
      trim: true,
      disabled: (): boolean => editor.handleExternalOrderNumber(),
      validators: [editor.validationService.maxLength(INPUT_MAX_LENGTH)],
      widgetData: {
        artificialInputCheck: true,
      },
    },
    status: {
      title: I18n.Common.Status,
      disabled: true,
      defaultValue: ORDER_STATUS_VALUE.New,
      widget: WIDGET_COMPONENT.select,
      selectOptions: editor.transactionStatusOptions,
      required: false,
    },
    internal_number: {
      omit: true,
      show: false,
    },
  };

  const vendorPanelSectionFields: FieldList = {
    vendor_panel_section: {
      title: I18n.Common.General,
      widget: WIDGET_COMPONENT.panel_group,
    },
    order_items_section: {
      title: '',
      widget: WIDGET_COMPONENT.panel_group,
    },
  };
  const summaryPanelFields: FieldList = {
    [editor.formFieldPath.virtualTotalCostShow]: {
      title: I18n.Forms.GridForm.TotalItemsCost,
      hiddenObs: editor.hiddenSummary$,
      disabled: true,
    },
    delivery_fee: {
      title: I18n.Forms.GridForm.DeliveryFee,
      widget: WIDGET_COMPONENT.input_currency,
      widgetFamily: WIDGET_FAMILY.XUX,
      hideArrows: true,
      hiddenObs: editor.hiddenSummary$,
      placeholder: '0',
      defaultValue: null,
      type: 'text',
      disabled: (): boolean => editor.summaryDisabled,
      validators: [
        editor.validationService.minValidator(0),
        editor.validationService.numberValidator(),
      ],
      minimum: 0,
      maximum: generateMaximumByPreferencesPrecision(
        MAX_INT_NUMBER,
        formService.stateService.getCurrencyPrecision(),
      ),
      widgetData: {
        inputClass: 'text-number',
        artificialInputCheck: true,
        currencySymbolFn: (): string =>
          formService.stateService.getCurrencySymbol(),
      },
    },
    tax_fee: {
      title: I18n.Forms.GridForm.SalesTax,
      widget: WIDGET_COMPONENT.input_currency,
      widgetFamily: WIDGET_FAMILY.XUX,
      hiddenObs: editor.hiddenSummary$,
      placeholder: '0',
      defaultValue: null,
      hideArrows: true,
      type: 'text',
      disabled: (): boolean => editor.summaryDisabled,
      validators: [
        editor.validationService.minValidator(0),
        editor.validationService.numberValidator(),
      ],
      // maybe not in use variable. in xux-input-currency component > input-currency.component
      minimum: 0,
      maximum: generateMaximumByPreferencesPrecision(
        MAX_INT_NUMBER,
        formService.stateService.getCurrencyPrecision(),
      ),
      widgetData: {
        inputClass: 'text-number',
        artificialInputCheck: true,
        currencySymbolFn: (): string =>
          formService.stateService.getCurrencySymbol(),
      },
    },
    [editor.formFieldPath.virtualGrandTotal]: {
      title: I18n.Common.GrandTotal,
      widget: WIDGET_COMPONENT.input_number,
      hiddenObs: editor.hiddenSummary$,
      disabled: true,
    },
  };
  // all section configuration
  const vendorAllPanelSection: IFormGroupRules = {
    name: I18n.Common.General,
    fields: keys(vendorPanelSectionFields),
  };

  const vendorPanelGroup: IFormGroupRules = {
    name: I18n.Common.General,
    id: 'vendor_panel_section', // target section name
    fields: keys(vendorPanelFields),
  };

  const orderListPanelFields: FieldList = {
    remove_message: {
      widget: WIDGET_COMPONENT.label,
      widgetFamily: WIDGET_FAMILY.LEGACY,
      titleObs: editor.removeInfoTitle$,
      hiddenObs: editor.hiddenRemoveInfo$,
      show: true,
      widgetData: {
        itemClass: COMMON_WARN_MESSAGE_CLASS,
      },
    },
    modified_message: {
      widget: WIDGET_COMPONENT.label,
      widgetFamily: WIDGET_FAMILY.LEGACY,
      titleObs: editor.modifiedInfoTitle$,
      hiddenObs: editor.hiddenModifiedInfo$,
      show: true,
      widgetData: {
        itemClass: COMMON_WARN_MESSAGE_CLASS,
      },
    },
    inventory_items: {
      widget: WIDGET_COMPONENT.edit_grid_custom,
      title: I18n.Forms.GridForm.ItemList,
      minItems: 0,
      class: 'one-column-tree-grid order-items-grid navigate-grid',
      widgetData: {
        gridHeight: DEFAULT_ITEM_LIST_TABLE_HEIGHT,
        gridMinHeight: DEFAULT_GRID_HEIGHT,
        showLoader: editor.isInventoryItemVenuesGridBusy$,
        useVisibleItemCountDescription: true,
        fieldChangeRefreshCellCondition: { item_entity_id: true },
        showRowOptions: true,
        showSearch: true,
        multiselect: false,
        orderControls: false,
        treeViewMode: formService.isCategoryListView(),
        defaultExpanded: false,
        disableColumnSelector: true,
        treeChildrenPropertyName: 'children',
        ignoreItemValueOptimization: true,
        artificialInputCheckForSearchPanel: true,
        inlineEditOptions: {
          beforeActionMappers: [formService.getInlineChangeMapper(editor)],
        },
        userGridOptions: {
          deltaRowDataMode: false,
          suppressMovableColumns: true,
          suppressHorizontalScroll: false,
          suppressClickEdit: true,
          suppressRowTransform: true,
          suppressRowClickSelection: true,
          defaultColDef: {
            editable: true,
          },
          noRowsOverlayComponent: NO_ROWS_OVERLAY.text,
          noRowsOverlayComponentParams: {
            noRowsMessageFunc:
              formService.listGridNoRowsOverlayMessageFunc.bind(formService),
          },
          // this can prevent list scroll when update row data
          immutableData: true,
          disableColumnSelector: true,
          singleClickEdit: true,
          rowClassRules: {
            ...getListGridRowCellClass(),
            ...getScanningHighlightRowClass(formService.scanningService),
          },
        },
        eventHandlers: {
          [XUX_GRID_ACTIONS.GRID_READY]: formService
            .getGridReadyHandler(editor)
            .bind(formService),
        },
        columnsObs: editor.orderItemListColumnObs,
        buttons: [
          {
            position: TOOLBAR_BUTTON_POSITION.HEADER,
            class: 'btn-primary',
            caption: I18n.Common.AddItemsBrackets,
            disabled: !editor.canManage,
            hidden: (): boolean =>
              get(editor, 'document.status') !== ORDER_STATUS_VALUE.Pending,
            click: editor.openOrderAddPendingItemsPage.bind(editor),
          },
        ],
      },
      suppressNoPermissionsDisable: true,
      validators: [
        formService.getInventoryItemVenuesRequireValidator().bind(editor),
      ],
    },
    ...totalsFormRules.fields,
    summary_section: {
      widget: WIDGET_COMPONENT.label,
      widgetFamily: WIDGET_FAMILY.LEGACY,
      title: I18n.Forms.OrderForm.OrderSummary,
      hiddenObs: editor.hiddenSummary$,
      widgetData: {
        itemClass: 'panel-inner-title',
      },
    },
    ...summaryPanelFields,
  };

  const orderListPanelGroup: IFormGroupRules = {
    name: I18n.Common.General,
    id: 'order_items_section',
    fields: keys(orderListPanelFields),
  };

  // combine all your section declaration here
  const sections: IFormGroupRules[] = [vendorAllPanelSection];

  // combine all your group declaration here
  const groups: IFormGroupRules[] = [vendorPanelGroup, orderListPanelGroup];

  return {
    fields: {
      ...vendorPanelSectionFields,
      ...vendorPanelFields,
      ...orderListPanelFields,
    },
    groups: [...sections, ...groups],
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        ...totalsFormRules.virtual?.properties,
        inventory_items: {
          type: 'array',
        },
        total_cost_show: {
          type: 'string',
        },
        grand_total: {
          type: 'string',
        },
      },
    },
  };
};
